import React, { useState, useEffect } from "react";
import { useNavigate, NavigateFunction } from "react-router-dom";
import {
  DataGrid,
  GridColDef,
  GridSortModel,
  GridPaginationModel,
  GridActionsCellItem,
  GridRowId,
  GridRenderCellParams
} from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import {Link, TextField, Chip, FormControl, InputLabel, Select, MenuItem, SelectChangeEvent} from "@mui/material";
import IPage from "../../types/page.type";
import {Box, Breadcrumbs, Button, Grid, Typography} from "@mui/joy";
import { Experimental_CssVarsProvider as CssVarsProvider } from "@mui/material/styles";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import { getServiceOrders, deleteById } from "../../services/service-order.service";
import SearchIcon from "@mui/icons-material/Search";
import IServiceOrder from "../../types/service-order.type";
import showNotification from "../utils/Notify";
import ConfirmationModal from "../utils/ConfirmationModal";
import {ServiceOrderStatusColors} from "../../types/enumeration/ServiceOrderStatusColors";
import {ServiceOrderStatusNames} from "../../types/enumeration/ServiceOrderStatusNames";
import {ServiceOrderStatus} from "../../types/enumeration/ServiceOrderStatus";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";

const ServiceOrdersList: React.FC = () => {
  let navigate: NavigateFunction = useNavigate();
  const [serviceOrdersPage, setServiceOrdersPage] = useState<IPage>({ content: [], totalElements: 0});
  const [loading, setLoading] = useState(false);
  const [sortColumn, setSortColumn] = useState("id");
  const [sortDirection, setSortDirection] = useState<any>("asc");
  const [keywordFilter, setKeywordFilter] = useState( "");
  const [statusFilter, setStatusFilter] = useState( "-1");
  const [confirmationModalOpen, setConfirmationModalOpen] = React.useState(false);
  const [selectedServiceOrderId, setSelectedServiceOrderId] = React.useState<any>();
  const [excludeClosedFilter, setExcludeClosedFilter] = useState( "true");

  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 10,
  });

  const columns: GridColDef<IServiceOrder>[] = [
    {
      field: 'id',
      headerName: 'ID',
      headerAlign: 'center',
      align: 'center',
      maxWidth: 60,
      flex: 1,
    },
    {
      field: 'number',
      headerName: 'Numero',
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      minWidth:160,
      flex: 1
    },
    {
      field: 'quoteNumber',
      headerName: 'Nro. de Cotizacion',
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      minWidth:160,
      flex: 1
    },
    {
      field: 'purchaseNumber',
      headerName: 'Nro. Orden de Compra',
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      minWidth:160,
      flex: 1
    },
    {
      field: 'status',
      headerName: 'Estado',
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      minWidth:160,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        return <Chip color={ServiceOrderStatusColors[ServiceOrderStatus[params.row.status as keyof typeof ServiceOrderStatus]]}
                     label={ServiceOrderStatusNames[ServiceOrderStatus[params.row.status as keyof typeof ServiceOrderStatus]]}
                     sx={{width: 160, fontWeight: 'bold'}}/>;
      }
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      headerAlign: 'center',
      align: 'center',
      minWidth: 20,
      flex: 1,
      cellClassName: 'actions',
      getActions: (params) => {
        const { id, status } = params.row;
        const actions = [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Edit"
            className="textPrimary"
            onClick={handleEditClick(id)}
            color="inherit"
          />
        ];
        if (status === ServiceOrderStatus.IN_PROGRESS) {
          actions.push(
            <GridActionsCellItem
              icon={<DeleteForeverIcon />}
              label="Delete"
              className="textPrimary"
              onClick={handleDeleteClick(id)}
              color="inherit"
            />
          )
        }
        return actions;
      },
    },
  ];

  const handleAddClick = () => {
    navigate('create');
  };

  const handleEditClick = (id: GridRowId) => () => {
    navigate('edit/'+id)
  };

  const handleConfirmationModalClose = () => {
    setSelectedServiceOrderId(undefined);
    setConfirmationModalOpen(false);
  }

  const handleDeleteClick = (id: GridRowId) => () => {
    setSelectedServiceOrderId(id);
    setConfirmationModalOpen(true);
  };

  const onConfirm = () => {
    deleteById(selectedServiceOrderId)
      .then(() => {
        showNotification({type: 'success', message: 'Orden de Servicio eliminada correctamente.'});
        handleConfirmationModalClose();
        fetchServiceOrders();
      })
      .catch(error => {
        showNotification({type: 'error', message: error.response.data.message});
      });
  };

  const handleChangeKeyword = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setKeywordFilter(event.target.value)
  };

  const handleChangeStatus = (event: SelectChangeEvent) => {
    setStatusFilter(event.target.value)
    if (event.target.value === "-1") {
      setExcludeClosedFilter("true")
    } else {
      setExcludeClosedFilter("false")
    }
  };

  const handleChangeExcludeClosed = (event: SelectChangeEvent) => {
    setExcludeClosedFilter(event.target.value)
  };

  const handlePaginationModelChange = (newPaginationModel: GridPaginationModel) => {
    setPaginationModel(newPaginationModel);
    fetchServiceOrders(newPaginationModel.page);
  };

  const handleSortModelChange = React.useCallback((sortModel: GridSortModel) => {
    let field = sortModel[0] ? sortModel[0].field : "id";
    let order = sortModel[0] ? sortModel[0].sort : "asc";
    fetchServiceOrders(paginationModel.page, paginationModel.pageSize, field, order);
    setSortColumn(field);
    setSortDirection(order);
  }, []);

  const fetchServiceOrders = async (page: number = paginationModel.page, size: number = paginationModel.pageSize, sort: string = sortColumn, order: any = sortDirection) => {
    setLoading(true);
    type Param = {[key: string] : string}
    const params: Param = {}
    params.page = String(page);
    params.size = String(size);
    params.sort = sort;
    params.order = order;

    if (statusFilter !== '-1') {
      params.status = statusFilter;
    }

    if (keywordFilter) {
      params.keyword = keywordFilter;
    }

    if (excludeClosedFilter) {
      params.excludeClosed = excludeClosedFilter;
    }

    getServiceOrders(params).then( (response) => {
      setServiceOrdersPage(response.data)
      setLoading(false);
    })
  };

  useEffect(() => {
    fetchServiceOrders();
  }, []);

  return (
    <Box
      sx={{
        px: { xs: 2},
        pt: { xs: 'calc(12px + var(--Header-height))' },
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        height: '100dvh',
        overflow: "auto"
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'start',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', px: { xs: 1} }}>
            <Breadcrumbs
              size="sm"
              aria-label="breadcrumbs"
              separator={<ChevronRightRoundedIcon/>}
              sx={{ pl: 0 }}
            >
              <Typography level="h4" component="h1">
                <DesignServicesIcon fontSize="small" sx={{ fontSize: 30, marginTop: -1}} />
              </Typography>
              <Typography level="h4" component="h1">
                Ordenes de Servicio
              </Typography>
              <Typography level="h4" component="h1">
                <Link
                  underline={"none"}
                  onClick={() => handleAddClick()}
                  sx={{ cursor: 'pointer' }}
                >
                  <AddCircleIcon fontSize="small" sx={{ fontSize: 30, marginTop: -1}} /> Crear Nuevo
                </Link>
              </Typography>
            </Breadcrumbs>
          </Box>

          <ConfirmationModal
            open={confirmationModalOpen}
            onConfirm={onConfirm}
            onCancel={handleConfirmationModalClose} />

          <Grid
            container
            rowSpacing={2}
            columnSpacing={2}
            marginBlock={1}
            marginInline={0}
            sx={{ width: '100%' }}
          >
            <Grid xl={5} md={6} sm={6} xs={12}>
              <CssVarsProvider>
                <TextField
                  fullWidth
                  id="keyword"
                  label="Buscar por Numero, Nro. Cotizacion y/o Nro. Orden de Compra"
                  name="keyword"
                  variant="filled"
                  type="text"
                  onChange={handleChangeKeyword}
                  onKeyDown={(ev) => {
                    if (ev.key === 'Enter') {
                      fetchServiceOrders();
                      ev.preventDefault();
                    }
                  }}
                  slotProps={{ inputLabel: {shrink: true}}}
                />
              </CssVarsProvider>
            </Grid>
            <Grid xl={2} md={6} sm={6} xs={12}>
              <CssVarsProvider>
                <FormControl variant="filled" fullWidth>
                  <InputLabel id="type-label" shrink>Estado</InputLabel>
                  <Select
                    labelId="type-label"
                    id="type"
                    name="type"
                    label="type"
                    value={statusFilter}
                    onChange={handleChangeStatus}
                  >
                    <MenuItem key="-1" value="-1">Todos</MenuItem>
                    {Object
                      .keys(ServiceOrderStatus)
                      .map((key) => (
                        <MenuItem key={key} value={ServiceOrderStatus[key as keyof typeof ServiceOrderStatus]}>
                          {ServiceOrderStatusNames[ServiceOrderStatus[key as keyof typeof ServiceOrderStatus]]}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </CssVarsProvider>
            </Grid>

            <Grid xl={2} md={6} sm={6} xs={12}>
              <CssVarsProvider>
                <FormControl variant="filled" fullWidth>
                  <InputLabel id="excludeClosed-label" shrink>Ocultar Cerradas</InputLabel>
                  <Select
                    labelId="excludeClosed-label"
                    id="excludeClosed"
                    name="excludeClosed"
                    label="excludeClosed"
                    value={excludeClosedFilter}
                    onChange={handleChangeExcludeClosed}
                    disabled={statusFilter !== '-1'}
                  >
                    <MenuItem key="true" value="true">Si</MenuItem>
                    <MenuItem key="false" value="false">No</MenuItem>
                  </Select>
                </FormControl>
              </CssVarsProvider>
            </Grid>

            <Grid xl={3} md={6} sm={6} xs={12}>
              <Button
                variant="outlined"
                fullWidth
                style={{height: 56}}
                endDecorator={<SearchIcon />}
                onClick={() => fetchServiceOrders()}
              >
                Buscar
              </Button>
            </Grid>
          </Grid>

          <Box sx={{ width: '100%', height: 632, typography: 'body1' }} ml={1} mt={0} pr={2}>
            <CssVarsProvider>
              <DataGrid
                rows={serviceOrdersPage.content}
                columns={columns}
                columnVisibilityModel={{
                  id: false,
                }}
                paginationModel={paginationModel}
                rowCount={serviceOrdersPage.totalElements}
                sortingMode="server"
                onSortModelChange={handleSortModelChange}
                paginationMode="server"
                onPaginationModelChange={handlePaginationModelChange}
                loading={loading}
                disableRowSelectionOnClick
              />
            </CssVarsProvider>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ServiceOrdersList;
